// noinspection DuplicatedCode
// Basiert auf https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev

import HumanReadableTime from './HumanReadableTime';
import Icon from './icon';

export const RUN_ALL = 'all';
export const RUN_FULL = 'full';
export const RUN_HALF = 'half';
export const RUN_RELAY = 'relay';

export const RUNS = [RUN_FULL, RUN_HALF, RUN_RELAY];
export const RUNS_WITH_ALL = [RUN_ALL, RUN_FULL, RUN_HALF, RUN_RELAY];

export const WAYPOINT_TYPE_TIME = 'time';
export const WAYPOINT_TYPE_START = 'start';
export const WAYPOINT_TYPE_FINISH = 'finish';
export const WAYPOINT_TYPE_HANDOVER = 'handover';
export const WAYPOINT_TYPE_CHARITY = 'charity';

export const RUNS_HUMAN_READABLE = {
    [RUN_FULL]: 'Marathon',
    [RUN_HALF]: 'Halbmarathon',
    [RUN_RELAY]: 'Staffelmarathon',
};

export interface WaypointInterface {
    distance: number;
    position: google.maps.LatLng;
}

export class Waypoint implements WaypointInterface {
    public position: google.maps.LatLng;

    public type: string;

    public distanceFull?: number;

    public distanceHalf?: number;

    public runs: string[];

    public run: string;

    public time: HumanReadableTime = new HumanReadableTime(0);

    public eta: HumanReadableTime = new HumanReadableTime(0);

    public deltaTime: HumanReadableTime = new HumanReadableTime(0);

    public passed: boolean = false;

    constructor(init?: Partial<Waypoint>) {
        Object.assign(this, init);
    }

    get distance(): number|undefined {
        if (this.run === RUN_HALF) {
            return this.distanceHalf;
        }

        return this.distanceFull;
    }

    static generateMarkerIconSmall(url: string): google.maps.Icon {
        return {
            url,
            scaledSize: new google.maps.Size(41, 45),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20.5, 22.5),
        };
    }

    static generateMarkerIconBig(url: string): google.maps.Icon {
        return {
            url,
            scaledSize: new google.maps.Size(73, 81),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(36.5, 40.5),
        };
    }

    getMarkerOptions(markerOptions: Partial<google.maps.MarkerOptions> = {}): google.maps.MarkerOptions {
        const defaultMarkerOptions = {
            icon: this.getMarkerIcon(),
            position: this.position,
            disableAutoPan: true,
        };

        return {...defaultMarkerOptions, ...markerOptions};
    }

    getMarkerIcon(run: string = this.run): google.maps.Icon {
        const colorVariant = Icon.getIconVariantForRun(run);

        switch (this.type) {
            case WAYPOINT_TYPE_TIME:
                if (this.passed) {
                    return Waypoint.generateMarkerIconSmall(Icon.getUrl('TIME_PASSED', colorVariant));
                }
                return Waypoint.generateMarkerIconSmall(Icon.getUrl('TIME_NORMAL', colorVariant));
            case WAYPOINT_TYPE_START:
                return Waypoint.generateMarkerIconBig(Icon.getUrl('START', colorVariant));
            case WAYPOINT_TYPE_FINISH:
                return Waypoint.generateMarkerIconBig(Icon.getUrl('FINISH', colorVariant));
            case WAYPOINT_TYPE_HANDOVER:
                return Waypoint.generateMarkerIconSmall(Icon.getUrl('HANDOVER', colorVariant));
            case WAYPOINT_TYPE_CHARITY:
                return Waypoint.generateMarkerIconSmall(Icon.getUrl('CHARITY', colorVariant));
            default:
                return null;
        }
    }

    isUsedForRun(run: string): boolean {
        return this.runs.includes(run);
    }
}

export function WAYPOINTS(): Array<Waypoint> {
    if (typeof google === 'undefined') return [];

    return [
        new Waypoint({
            distanceFull: 0,
            distanceHalf: 0,
            position: new google.maps.LatLng({
                lat: 50.9398817,
                lng: 6.9741267,
            }),
            runs: [RUN_ALL, RUN_FULL, RUN_HALF, RUN_RELAY],
            type: WAYPOINT_TYPE_START,
        }),
        new Waypoint({
            distanceFull: 0.5,
            distanceHalf: 0.5,
            position: new google.maps.LatLng({
                lat: 50.9365395,
                lng: 6.9705587,
            }),
            runs: [RUN_ALL, RUN_FULL, RUN_HALF, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 5,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.9061655,
                lng: 6.9783196,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 10,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.911545,
                lng: 6.969737,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 11.7,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.922122,
                lng: 6.959407,
            }),
            runs: [RUN_RELAY],
            type: WAYPOINT_TYPE_HANDOVER,
        }),
        new Waypoint({
            distanceFull: 15,
            distanceHalf: 2.6,
            position: new google.maps.LatLng({
                lat: 50.9359034,
                lng: 6.9409383,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 21.0975,
            distanceHalf: 8.7,
            position: new google.maps.LatLng({
                lat: 50.9318539,
                lng: 6.9257796,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: -1,
            distanceHalf: 10,
            position: new google.maps.LatLng({
                lat: 50.934066,
                lng: 6.913564,
            }),
            runs: [RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 22.9,
            distanceHalf: 11.2,
            position: new google.maps.LatLng({
                lat: 50.936622,
                lng: 6.920375,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_CHARITY,
        }),
        new Waypoint({
            distanceFull: 23.4,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.936189,
                lng: 6.929579,
            }),
            runs: [RUN_RELAY],
            type: WAYPOINT_TYPE_HANDOVER,
        }),
        new Waypoint({
            distanceFull: 25,
            distanceHalf: 12.6,
            position: new google.maps.LatLng({
                lat: 50.9395143,
                lng: 6.939664,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 29.8,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.9500283,
                lng: 6.9549945,
            }),
            runs: [RUN_RELAY],
            type: WAYPOINT_TYPE_HANDOVER,
        }),
        new Waypoint({
            distanceFull: 30,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.950423,
                lng: 6.956246,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 35,
            distanceHalf: -1,
            position: new google.maps.LatLng({
                lat: 50.9678301,
                lng: 6.9589346,
            }),
            runs: [RUN_FULL, RUN_RELAY],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 40,
            distanceHalf: 18.9,
            position: new google.maps.LatLng({
                lat: 50.9365418,
                lng: 6.9390769,
            }),
            runs: [RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_TIME,
        }),
        new Waypoint({
            distanceFull: 42.195,
            distanceHalf: 21.0975,
            position: new google.maps.LatLng({
                lat: 50.9415688,
                lng: 6.953914,
            }),
            runs: [RUN_ALL, RUN_FULL, RUN_RELAY, RUN_HALF],
            type: WAYPOINT_TYPE_FINISH,
        }),
    ];
}

export function findWaypointForPosition(waypoints: Waypoint[], position: google.maps.LatLng): Waypoint | null {
    if (!waypoints) return null;
    return waypoints.find((waypoint) => waypoint.position.equals(position)) || null;
}

export function getWaypointsForRun(run: string): Array<Waypoint> {
    // Filter for waypoints used for this run
    const waypointsForRun = WAYPOINTS().filter((waypoint) => waypoint.runs.indexOf(run) !== -1);

    // Set Waypoint.run
    return waypointsForRun.map((waypoint) => new Waypoint({...waypoint, run}));
}

export function getWaypointDistancesForRun(run: string): Array<number> {
    return getWaypointsForRun(run)
        .filter((waypoint) => waypoint.type !== WAYPOINT_TYPE_CHARITY)
        .map((waypoint) => waypoint.distance)
        .slice(1);
}
